import type { SearchHit } from "@models/search";

/** Colour bands for a measured similarity, as a percentage. */
export function similarityColor(pct: number): string {
  if (pct >= 70) return "teal";
  if (pct >= 50) return "brand";
  return "gray";
}

export interface ScoreBadge {
  label: string;
  color: string;
  /** Why the label reads the way it does; surfaced as a tooltip. */
  hint?: string;
}

/**
 * What to put in a hit's score badge, given what the score is evidence OF.
 *
 * Three cases, and conflating them is what made a correct row read 61%:
 *
 * - **`exact_match`** — the record IS the queried value, decided arithmetically
 *   by the service over its whole retrieval pool. That is a decision, not a
 *   distance, so it reads **100%**. The cosine beside it understates it badly:
 *   an asymmetric embedding caps its own self-similarity around 0.74, so a
 *   byte-identical hit would otherwise render as "74%".
 * - **no measurement** — the re-score probe never returned this record (S3
 *   Vectors' filtered-ANN recall hole). Unknown, not bad. Render a dash:
 *   `Math.round(null * 100)` prints a confident `0%` for a record nobody
 *   scored, which reads as "terrible match" for one that may be the answer.
 * - **otherwise** — the calibrated cosine, as a percentage.
 */
export function scoreBadge(hit: Pick<SearchHit, "similarity" | "scoreBasis">): ScoreBadge {
  if (hit.scoreBasis === "exact_match") {
    return {
      label: "100%",
      color: "teal",
      hint: "Exact value match, decided by the service, not by a cosine",
    };
  }
  if (hit.similarity == null) {
    return {
      label: "-",
      color: "gray",
      hint: "No cosine was measured for this record: unknown, not low",
    };
  }
  const pct = Math.round(hit.similarity * 100);
  return { label: `${pct}%`, color: similarityColor(pct) };
}
